import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { PageIntro } from '@/components/help/PageIntro';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { Button } from '@/components/ui/button';
import { PlannerCard } from '@/components/planner/PlannerCard';
import { QuickIdeaDialog } from '@/components/planner/QuickIdeaDialog';
import {
  socialApi,
  type BoardStatus,
  type PlannerData,
  type SocialPlatform,
  type SocialPost,
} from '@/lib/social-api';
import { PLATFORM_ORDER, platformMeta } from '@/lib/social-platforms';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Inbox,
  Loader2,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';

type ViewMode = 'calendar' | 'board';

const BOARD_LANES: Array<{ id: BoardStatus; label: string; hint: string }> = [
  { id: 'idea', label: 'Ideen', hint: 'Gesammelte Themen ohne festes Datum.' },
  { id: 'draft', label: 'In Arbeit', hint: 'Text und Medien entstehen gerade.' },
  { id: 'review', label: 'Review', hint: 'Wartet auf Gegenlesen im Team.' },
  { id: 'approved', label: 'Freigegeben', hint: 'Inhaltlich abgenommen, bereit zum Einplanen.' },
  { id: 'scheduled', label: 'Geplant', hint: 'Termin steht, Veröffentlichung ist eingereiht.' },
  { id: 'published', label: 'Veröffentlicht', hint: 'Auf allen Kanälen raus.' },
];

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Behält die Uhrzeit eines Posts bei, wenn er auf einen anderen Tag wandert. */
function combineDateWithExistingTime(day: Date, existing?: string | null): string {
  const next = new Date(day);
  if (existing) {
    const prev = new Date(existing);
    next.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
  } else {
    next.setHours(9, 0, 0, 0);
  }
  return next.toISOString();
}

interface ContentPlannerPageProps {
  onNavigate: (path: string) => void;
}

export function ContentPlannerPage({ onNavigate }: ContentPlannerPageProps) {
  const [view, setView] = useState<ViewMode>('calendar');
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [data, setData] = useState<PlannerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [ideaDate, setIdeaDate] = useState<string | null>(null);

  const [pillarFilter, setPillarFilter] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<SocialPlatform | null>(null);
  const [search, setSearch] = useState('');

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  const gridDays = useMemo(() => {
    const rangeStart = startOfMonth(cursor);
    const rangeEnd = endOfMonth(cursor);
    const start = addDays(rangeStart, -((rangeStart.getDay() + 6) % 7));
    const end = endOfWeek(rangeEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const load = useCallback(async () => {
    const rangeStart = startOfMonth(cursor);
    const rangeEnd = endOfMonth(cursor);
    const from = addDays(rangeStart, -14).toISOString();
    const to = addDays(rangeEnd, 14).toISOString();
    try {
      setData(await socialApi.planner(from, to));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Planner konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const matchesFilters = useCallback(
    (post: SocialPost) => {
      if (pillarFilter && post.pillar_id !== pillarFilter) return false;
      if (platformFilter && !post.targets.some((t) => t.platform === platformFilter)) return false;
      if (search.trim()) {
        const haystack = `${post.title ?? ''} ${post.body ?? ''} ${post.notes ?? ''}`.toLowerCase();
        if (!haystack.includes(search.trim().toLowerCase())) return false;
      }
      return true;
    },
    [pillarFilter, platformFilter, search]
  );

  const planned = useMemo(() => (data?.planned ?? []).filter(matchesFilters), [data, matchesFilters]);
  const backlog = useMemo(() => (data?.backlog ?? []).filter(matchesFilters), [data, matchesFilters]);

  const postsByDay = useMemo(() => {
    const map = new Map<string, SocialPost[]>();
    for (const post of planned) {
      if (!post.planned_at) continue;
      const key = format(new Date(post.planned_at), 'yyyy-MM-dd');
      const list = map.get(key);
      if (list) list.push(post);
      else map.set(key, [post]);
    }
    return map;
  }, [planned]);

  const postsByLane = useMemo(() => {
    const map = new Map<BoardStatus, SocialPost[]>();
    for (const lane of BOARD_LANES) map.set(lane.id, []);
    for (const post of [...planned, ...backlog]) {
      const lane = (post.board_status ?? 'idea') as BoardStatus;
      map.get(lane)?.push(post);
    }
    return map;
  }, [planned, backlog]);

  /** Slots pro Wochentag — zeigt leere Redaktionsplätze im Kalender an. */
  const slotsByWeekday = useMemo(() => {
    const map = new Map<number, PlannerData['slots']>();
    for (const slot of data?.slots ?? []) {
      const list = map.get(slot.weekday);
      if (list) list.push(slot);
      else map.set(slot.weekday, [slot]);
    }
    return map;
  }, [data]);

  const applyMove = async (
    postId: string,
    payload: Parameters<typeof socialApi.movePlannerPost>[1]
  ) => {
    setBusy(true);
    // Optimistisch aktualisieren, damit die Karte nicht zurückspringt.
    setData((prev) => {
      if (!prev) return prev;
      const patch = (p: SocialPost): SocialPost =>
        p.id === postId ? { ...p, ...payload } : p;
      return { ...prev, planned: prev.planned.map(patch), backlog: prev.backlog.map(patch) };
    });
    try {
      await socialApi.movePlannerPost(postId, payload);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Verschieben fehlgeschlagen.');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const postId = draggableId.replace(/^post:/, '');
    const post =
      planned.find((p) => p.id === postId) ?? backlog.find((p) => p.id === postId) ?? null;

    if (destination.droppableId === 'backlog') {
      void applyMove(postId, { planned_at: null });
      return;
    }

    if (destination.droppableId.startsWith('day:')) {
      const day = new Date(`${destination.droppableId.slice(4)}T00:00:00`);
      void applyMove(postId, {
        planned_at: combineDateWithExistingTime(day, post?.planned_at),
      });
      return;
    }

    if (destination.droppableId.startsWith('lane:')) {
      const lane = destination.droppableId.slice(5) as BoardStatus;
      if (lane === 'published') {
        toast.error('„Veröffentlicht“ wird automatisch gesetzt, sobald alle Kanäle raus sind.');
        return;
      }
      void applyMove(postId, { board_status: lane });
    }
  };

  const openPost = (post: SocialPost) => onNavigate(`/composer/${post.id}`);

  const stats = data?.stats ?? {};
  const pillars = data?.pillars ?? [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageIntro
        title="Content-Planner"
        description="Von der Idee bis zur Veröffentlichung: Themen sammeln, im Kalender platzieren, freigeben und auf allen Kanälen ausspielen."
        className="max-w-none"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void load()} disabled={busy}>
              <RefreshCw size={16} className={cn('mr-1.5', busy && 'animate-spin')} /> Aktualisieren
            </Button>
            <Button
              onClick={() => {
                setIdeaDate(null);
                setIdeaOpen(true);
              }}
            >
              <Plus size={16} className="mr-1.5" /> Neue Idee
            </Button>
          </div>
        }
      />

      {/* Kennzahlen als Schnellfilter über den Board-Status */}
      <div className="mb-5 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {BOARD_LANES.map((lane) => (
          <button
            key={lane.id}
            type="button"
            onClick={() => setView('board')}
            title={lane.hint}
            className="rounded-lg border border-border bg-card px-3 py-2.5 text-left transition hover:border-border"
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted">{lane.label}</p>
            <p className="mt-0.5 text-2xl font-semibold tabular-nums text-foreground">
              {stats[lane.id] ?? 0}
            </p>
          </button>
        ))}
      </div>

      {/* Steuerleiste */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border p-0.5">
            <button
              type="button"
              onClick={() => setView('calendar')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition',
                view === 'calendar'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted'
              )}
            >
              <CalendarDays size={15} /> Kalender
            </button>
            <button
              type="button"
              onClick={() => setView('board')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition',
                view === 'board'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted'
              )}
            >
              <Columns3 size={15} /> Board
            </button>
          </div>

          {view === 'calendar' && (
            <div className="flex items-center gap-1">
              <Button aria-label="Vorheriger Monat" variant="outline" size="sm" onClick={() => setCursor(startOfMonth(addDays(monthStart, -1)))}>
                <ChevronLeft size={16} />
              </Button>
              <span className="min-w-[9.5rem] text-center text-sm font-semibold capitalize text-foreground">
                {format(cursor, 'MMMM yyyy', { locale: de })}
              </span>
              <Button aria-label="Nächster Monat" variant="outline" size="sm" onClick={() => setCursor(startOfMonth(addDays(monthEnd, 1)))}>
                <ChevronRight size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCursor(startOfMonth(new Date()))}>
                Heute
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen…"
              className="w-40 rounded-lg border border-border bg-card py-1.5 pl-8 pr-2 text-sm"
            />
          </div>

          {pillars.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {pillars.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPillarFilter(pillarFilter === p.id ? null : p.id)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium transition',
                    pillarFilter === p.id
                      ? 'border-border bg-primary text-primary-foreground'
                      : 'border-border text-muted'
                  )}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                  {p.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-1">
            {PLATFORM_ORDER.map((p) => {
              const meta = platformMeta(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatformFilter(platformFilter === p ? null : p)}
                  title={meta.label}
                  className={cn(
                    'rounded-lg border px-2 py-1 text-xs font-medium transition',
                    platformFilter === p
                      ? 'border-border bg-primary text-primary-foreground'
                      : 'border-border text-muted'
                  )}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className={cn('grid gap-4', view === 'calendar' && 'xl:grid-cols-[1fr_280px]')}>
          <div className="min-w-0">
            {view === 'calendar' ? (
              <CalendarGrid
                days={gridDays}
                cursor={cursor}
                postsByDay={postsByDay}
                slotsByWeekday={slotsByWeekday}
                onOpen={openPost}
                onAddOn={(day) => {
                  setIdeaDate(format(day, "yyyy-MM-dd'T'09:00"));
                  setIdeaOpen(true);
                }}
              />
            ) : (
              <BoardView lanes={BOARD_LANES} postsByLane={postsByLane} onOpen={openPost} />
            )}
          </div>

          {view === 'calendar' && <BacklogRail
            posts={backlog}
            onOpen={openPost}
            onAdd={() => {
              setIdeaDate(null);
              setIdeaOpen(true);
            }}
          />}
        </div>
      </DragDropContext>

      <QuickIdeaDialog
        open={ideaOpen}
        onOpenChange={setIdeaOpen}
        pillars={pillars}
        defaultDate={ideaDate}
        onCreated={() => void load()}
      />
    </div>
  );
}

// ─── Kalender ────────────────────────────────────────────────────────────────

function CalendarGrid({
  days,
  cursor,
  postsByDay,
  slotsByWeekday,
  onOpen,
  onAddOn,
}: {
  days: Date[];
  cursor: Date;
  postsByDay: Map<string, SocialPost[]>;
  slotsByWeekday: Map<number, PlannerData['slots']>;
  onOpen: (post: SocialPost) => void;
  onAddOn: (day: Date) => void;
}) {
  const today = new Date();

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="grid grid-cols-7 border-b border-border bg-background dark:bg-card">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-muted">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-card-hover">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const posts = postsByDay.get(key) ?? [];
          const inMonth = isSameMonth(day, cursor);
          const isToday = isSameDay(day, today);
          const isoWeekday = ((day.getDay() + 6) % 7) + 1;
          const slots = slotsByWeekday.get(isoWeekday) ?? [];

          return (
            <Droppable droppableId={`day:${key}`} key={key}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={cn(
                    'group/day min-h-[130px] bg-card p-1.5 transition-colors dark:bg-background',
                    !inMonth && 'bg-background/70 dark:bg-card/50',
                    snapshot.isDraggingOver && 'bg-card-hover'
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span
                      className={cn(
                        'flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-medium',
                        isToday
                          ? 'bg-primary text-primary-foreground'
                          : inMonth
                            ? 'text-muted dark:text-foreground'
                            : 'text-muted'
                      )}
                    >
                      {format(day, 'd')}
                    </span>
                    <button
                      type="button"
                      onClick={() => onAddOn(day)}
                      className="rounded p-0.5 text-muted opacity-0 transition hover:bg-card-hover hover:text-foreground group-hover/day:opacity-100"
                      aria-label={`Idee für ${format(day, 'd. MMMM', { locale: de })} anlegen`}
                    >
                      <Plus size={13} />
                    </button>
                  </div>

                  {posts.length === 0 && slots.length > 0 && inMonth && (
                    <div className="mb-1 space-y-0.5">
                      {slots.slice(0, 2).map((slot) => (
                        <p
                          key={slot.id}
                          className="truncate rounded border border-dashed border-border px-1 py-0.5 text-[10px] text-muted"
                          title={`Freier Redaktions-Slot: ${slot.label}`}
                        >
                          {slot.time_of_day.slice(0, 5)} {slot.label}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="space-y-1">
                    {posts.map((post, index) => (
                      <Draggable draggableId={`post:${post.id}`} index={index} key={post.id}>
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                          >
                            <PlannerCard
                              post={post}
                              onOpen={onOpen}
                              compact
                              dragging={dragSnapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </div>
  );
}

// ─── Board ───────────────────────────────────────────────────────────────────

function BoardView({
  lanes,
  postsByLane,
  onOpen,
}: {
  lanes: typeof BOARD_LANES;
  postsByLane: Map<BoardStatus, SocialPost[]>;
  onOpen: (post: SocialPost) => void;
}) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex min-w-max gap-3">
        {lanes.map((lane) => {
          const posts = postsByLane.get(lane.id) ?? [];
          return (
            <div key={lane.id} className="w-64 shrink-0">
              <div className="mb-2 flex items-center justify-between px-1">
                <LabelWithHint label={`${lane.label} (${posts.length})`} hint={lane.hint} />
              </div>
              <Droppable droppableId={`lane:${lane.id}`}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      'min-h-[420px] space-y-2 rounded-lg border border-border bg-background/70 p-2 transition-colors dark:bg-card/40',
                      snapshot.isDraggingOver && 'border-border bg-card-hover'
                    )}
                  >
                    {posts.map((post, index) => (
                      <Draggable draggableId={`post:${post.id}`} index={index} key={post.id}>
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                          >
                            <PlannerCard post={post} onOpen={onOpen} dragging={dragSnapshot.isDragging} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {posts.length === 0 && (
                      <p className="px-2 py-6 text-center text-xs text-muted">Nichts hier.</p>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Backlog ─────────────────────────────────────────────────────────────────

function BacklogRail({
  posts,
  onOpen,
  onAdd,
}: {
  posts: SocialPost[];
  onOpen: (post: SocialPost) => void;
  onAdd: () => void;
}) {
  return (
    <aside className="min-w-0">
      <div className="mb-2 flex items-center justify-between">
        <LabelWithHint
          label={`Backlog (${posts.length})`}
          hint="Ideen ohne Datum. Zieh eine Karte in den Kalender, um sie einzuplanen."
        />
        <Button variant="ghost" size="sm" onClick={onAdd}>
          <Plus size={14} />
        </Button>
      </div>
      <Droppable droppableId="backlog">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              'min-h-[300px] space-y-2 rounded-lg border border-border bg-background/70 p-2 transition-colors dark:bg-card/40',
              snapshot.isDraggingOver && 'border-border bg-card-hover'
            )}
          >
            {posts.map((post, index) => (
              <Draggable draggableId={`post:${post.id}`} index={index} key={post.id}>
                {(dragProvided, dragSnapshot) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                  >
                    <PlannerCard post={post} onOpen={onOpen} dragging={dragSnapshot.isDragging} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {posts.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-2 py-10 text-center">
                <Inbox size={20} className="text-muted" />
                <p className="text-xs text-muted">
                  Keine offenen Ideen. Alles ist eingeplant.
                </p>
              </div>
            )}
          </div>
        )}
      </Droppable>
    </aside>
  );
}
